import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  ConfirmDialog,
  Empty,
  ErrorState,
  Loading,
  Modal,
  TextInput,
  useToast,
} from '../components/ui';
import { renderSubscriptionInvoice, type SubscriptionInvoiceView } from '@kortex/shared';
import { allowance, AllowanceMeter } from '../components/PlanAllowance';
import { api, apiUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, money } from '../lib/format';

/**
 * A hotel's own subscription: what it is on, what another period costs, and
 * what it has paid.
 *
 * No price or interval is written here. Everything comes from
 * /api/billing/options, so changing what a period costs is a console edit
 * rather than a release.
 */

interface BillingOption {
  id: string;
  label: string;
  intervalUnit: 'MONTH' | 'YEAR';
  intervalCount: number;
  price: number;
  taxRate: number;
  taxLabel: string;
  taxAmount: number;
  total: number;
  currency: string;
  supportsOneTime: boolean;
  supportsRecurring: boolean;
}

interface OptionsResponse {
  planCode: string | null;
  planName?: string;
  currentBillingOptionId: string | null;
  autoRenew: boolean;
  /** Which Cashfree gateway the server is set up against, so the SDK matches. */
  environment?: 'TEST' | 'LIVE';
  /** Standing instructions that can still charge. More than one bills twice. */
  canChangePlan?: boolean;
  plans?: Array<{
    id: string;
    code: string;
    name: string;
    description: string | null;
    priceMonthly: number;
    maxProperties: number;
    maxUsers: number;
    isCurrent: boolean;
    options: BillingOption[];
  }>;
  mandates?: Array<{
    id: string;
    status: string;
    label: string;
    startedAt: string;
    /** When it begins charging; null means on authorisation. */
    firstChargeAt: string | null;
    /** Authorised, but waiting for the current plan to run out. */
    queued: boolean;
    /** False for a checkout that was opened and never completed. */
    authorised: boolean;
    isCurrent: boolean;
  }>;
  options: BillingOption[];
}

/**
 * An invoice row, carrying everything a tax invoice has to print.
 *
 * The whole document comes with the list rather than from a second request:
 * the page has to render it anyway, and an invoice that needed another round
 * trip to be read is one somebody closes instead of reading.
 */
interface InvoiceRow extends SubscriptionInvoiceView {
  id: string;
  paymentMode: 'ONE_TIME' | 'RECURRING';
}

interface PaymentRow {
  id: string;
  type: string;
  paymentMode: 'ONE_TIME' | 'RECURRING';
  amount: number;
  status: string;
  reference: string;
  invoiceNumber: string | null;
  failureReason: string | null;
  createdAt: string;
}

interface CheckoutHandle {
  reference: string;
  paymentMode: 'ONE_TIME' | 'RECURRING';
  /** A one-off order, for `checkout`. */
  paymentSessionId?: string;
  /** A mandate, for `subscriptionsCheckout`. This is how recurring opens. */
  sessionId?: string | null;
  authorisationUrl?: string | null;
  amount: number;
  periodEndsAt: string;
}

/** Their SDK resolves rather than throws, and reports failure in the value. */
interface CashfreeResult {
  error?: { message?: string };
}

declare global {
  interface Window {
    Cashfree?: (config: { mode: string }) => {
      /** One-off orders, keyed by `payment_session_id`. */
      checkout: (options: Record<string, unknown>) => Promise<CashfreeResult>;
      /** Mandates, keyed by `subscription_session_id`. A different method. */
      subscriptionsCheckout: (options: Record<string, unknown>) => Promise<CashfreeResult>;
    };
  }
}

const SDK_SRC = 'https://sdk.cashfree.com/js/v3/cashfree.js';

/**
 * Loads Cashfree's own checkout script, once.
 *
 * Theirs rather than a form of ours, for the same reason every gateway
 * insists on it: card and UPI credentials must never touch this page.
 */
function loadCashfree(): Promise<boolean> {
  if (window.Cashfree) return Promise.resolve(true);
  return new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve(Boolean(window.Cashfree)));
    script.addEventListener('error', () => resolve(false));
    if (!existing) document.body.appendChild(script);
  });
}

export function BillingPage() {
  const { subscription, refreshSubscription, can } = useAuth();
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  /** Which plan the periods below belong to; null means the one they are on. */
  const [planId, setPlanId] = useState<string | null>(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);

  /*
   * The summary is fetched once, when the session loads. A hotel that has
   * added three users since signing in would be shown the count it had then,
   * which is worse than showing nothing - so this page, the one place the
   * figure is on screen, asks for a fresh one on arrival.
   */
  useEffect(() => {
    void refreshSubscription();
  }, [refreshSubscription]);

  const options = useQuery({
    queryKey: ['billing', 'options'],
    queryFn: () => api.get<OptionsResponse>('/api/billing/options'),
  });
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => api.get<InvoiceRow[]>('/api/billing/invoices'),
    enabled: can('billing:view'),
  });
  const payments = useQuery({
    queryKey: ['billing', 'payments'],
    queryFn: () => api.get<PaymentRow[]>('/api/billing/payments'),
    enabled: can('billing:view'),
  });

  const checkout = useMutation({
    mutationFn: (input: { billingOptionId: string; autoRenew: boolean }) =>
      api.post<CheckoutHandle>('/api/billing/checkout', {
        ...input,
        /*
         * The API's return route, not this page's address.
         *
         * Cashfree brings the browser back to a one-off purchase with a GET,
         * which this page handles, and back from authorising a mandate with a
         * POST, which it cannot: IIS serves this route from a static file and
         * answers a POST with "405 - the verb used to access this page is not
         * allowed". The customer has just authorised a recurring payment and
         * is shown an error page.
         *
         * The API route takes either method and answers 303, which turns the
         * browser's next request into a GET, and it lands back here properly.
         */
        returnUrl: new URL(apiUrl('/api/payments/return'), window.location.origin).toString(),
      }),
    onSuccess: async (handle) => {
      /*
       * Two sessions, two methods. A mandate is authorised through
       * `subscriptionsCheckout` with the subscription session id; a one-off
       * order goes through `checkout` with the payment session id. Either
       * way nothing is granted until their webhook says the money arrived.
       *
       * The mode has to match the environment the server minted the session
       * against: one made on the live gateway cannot be paid on the sandbox.
       */
      const mode = options.data?.environment === 'LIVE' ? 'production' : 'sandbox';

      if (handle.paymentMode === 'RECURRING') {
        if (handle.sessionId && (await loadCashfree())) {
          const result = await window.Cashfree!({ mode }).subscriptionsCheckout({
            subsSessionId: handle.sessionId,
            redirectTarget: '_self',
          });
          if (result?.error?.message) push(result.error.message, 'err');
          return;
        }
        // Kept as a fallback rather than the main route: their create-
        // subscription response carries a session id and no link, whatever
        // older integrations did.
        if (handle.authorisationUrl) {
          window.location.href = handle.authorisationUrl;
          return;
        }
      } else if (handle.paymentSessionId && (await loadCashfree())) {
        const result = await window.Cashfree!({ mode }).checkout({
          paymentSessionId: handle.paymentSessionId,
          redirectTarget: '_self',
        });
        if (result?.error?.message) push(result.error.message, 'err');
        return;
      }

      push('Checkout opened, but the payment window could not be shown.', 'err');
    },
    onError: fail,
  });

  const cancelRenewal = useMutation({
    mutationFn: () => api.post('/api/billing/cancel-renewal', {}),
    onSuccess: () => {
      push('Automatic renewal turned off', 'ok');
      setStopping(false);
      void client.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (caught) => {
      setStopping(false);
      fail(caught);
    },
  });

  if (options.isLoading) return <Loading />;
  if (options.error)
    return <ErrorState error={options.error} onRetry={() => void options.refetch()} />;

  const data = options.data!;
  /*
   * A hotel with nothing paid for may buy any plan, so the periods on offer
   * follow whichever plan is selected rather than whichever they signed up
   * on. Locked in mid-period, the server sends one plan and this collapses
   * back to what it always was.
   */
  const plans = data.plans ?? [];
  const chosenPlan = plans.find((plan) => plan.id === planId) ?? plans.find((p) => p.isCurrent);
  const available = chosenPlan ? chosenPlan.options : data.options;
  const chosen = available.find((o) => o.id === selected) ?? null;
  const canPay = can('settings:manage');
  // Auto-renew is only offerable for a period that supports it.
  const renewable = chosen?.supportsRecurring ?? false;
  const wantsRenew = autoRenew && renewable;

  return (
    <div className="stack">
      <div className="view-head">
        <div>
          <h1>Billing</h1>
          <div className="sub">Your SARTHI subscription, invoices and payments.</div>
        </div>
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <h2>Current subscription</h2>
            {subscription ? (
              <span className={`chip ${subscription.isEntitled ? 'ok' : 'danger'}`}>
                {subscription.statusLabel}
              </span>
            ) : null}
          </div>
          <div className="card-body col gap-12">
            {subscription ? (
              <>
                <div className="stat-line">
                  <span className="dim">Plan</span>
                  <b>{subscription.planName}</b>
                </div>
                <div className="stat-line">
                  <span className="dim">Access until</span>
                  <b>{subscription.endsAt ? date(subscription.endsAt) : '—'}</b>
                </div>
                <div className="stat-line">
                  <span className="dim">Days remaining</span>
                  <b>{subscription.daysRemaining}</b>
                </div>
                <div className="stat-line">
                  <span className="dim">Automatic renewal</span>
                  <b>{data.autoRenew ? 'On' : 'Off'}</b>
                </div>
                {/*
                 * What the plan allows, and how much of it is gone.
                 *
                 * The plan rows below sell the allowance of every *other*
                 * plan; the one the hotel is actually on said only what it
                 * cost. An owner deciding whether to upgrade needs to know
                 * they are at nine users of ten, and the only way to learn
                 * that was to be refused the tenth.
                 */}
                {subscription.usage ? (
                  <>
                    <AllowanceMeter
                      label="Properties"
                      what="properties"
                      allowance={allowance(
                        subscription.usage.properties,
                        subscription.limits.maxProperties,
                      )}
                    />
                    <AllowanceMeter
                      label="Users"
                      what="users"
                      allowance={allowance(subscription.usage.users, subscription.limits.maxUsers)}
                    />
                  </>
                ) : null}
                {/* More than one standing instruction means more than one
                    debit a period. The hotel has no other way to find that
                    out - the gateway runs those schedules, not us. */}
                {(data.mandates?.length ?? 0) > 1
                  ? (() => {
                      const all = data.mandates!;
                      /*
                       * Only the ones that can actually take money.
                       *
                       * An unauthorised mandate is an abandoned checkout - no
                       * instruction exists at the bank - and a queued one is
                       * waiting for the current plan to run out. Neither debits
                       * anybody this period, and counting them told a hotel it
                       * was being billed twice when it was not, then advised
                       * turning off renewal, which would have cancelled the one
                       * instruction that worked.
                       */
                      const charging = all.filter(
                        (mandate) => mandate.authorised && !mandate.queued,
                      );
                      const queued = all.filter((mandate) => mandate.queued);
                      /*
                       * Two that both charge now is a problem worth a warning.
                       * One charging and one waiting its turn is the hotel
                       * having lined up its next plan, which is just the truth
                       * about their account. An unfinished checkout on its own
                       * is neither, and saying nothing about it is right.
                       */
                      const overlapping = charging.length > 1;
                      if (!overlapping && queued.length === 0) return null;
                      return (
                        <div className={`form-note${overlapping ? ' warn' : ''}`}>
                          {overlapping ? (
                            <>
                              <b>
                                You have {charging.length} payment instructions charging on this
                                subscription.
                              </b>{' '}
                              Each is charged separately, so you are being billed {charging.length}{' '}
                              times per period. Turning off automatic renewal cancels all of them at
                              once; you keep every period already paid for.
                            </>
                          ) : (
                            <b>
                              Your payment instruction is set up and starts when your free trial
                              ends.
                            </b>
                          )}
                          <ul className="plan-features" style={{ marginTop: 8 }}>
                            {all.map((mandate) => (
                              <li key={mandate.id}>
                                {mandate.label} —{' '}
                                {!mandate.authorised
                                  ? 'never finished, so it charges nothing'
                                  : mandate.queued && mandate.firstChargeAt
                                    ? `starts ${date(mandate.firstChargeAt)}`
                                    : `charging since ${date(mandate.startedAt)}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()
                  : null}
                {data.autoRenew ? (
                  <>
                    <p className="small muted">
                      Your subscription renews automatically using your authorised payment method.
                    </p>
                    {canPay ? (
                      <button className="btn" onClick={() => setStopping(true)}>
                        Turn off automatic renewal
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p className="small muted">
                    Your subscription will not renew automatically. Buy another period below
                    whenever you are ready.
                  </p>
                )}

                {canPay ? (
                  <RedeemCoupon
                    onDone={() => {
                      // The banner, the dates and the subscription summary all
                      // move when time is added, and they come from two queries.
                      void client.invalidateQueries({ queryKey: ['billing'] });
                      void client.invalidateQueries({ queryKey: ['subscription'] });
                    }}
                  />
                ) : null}
              </>
            ) : (
              <Empty message="No subscription found for this hotel." />
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Buy a period</h2>
          </div>
          <div className="card-body col gap-16">
            {/*
             * Offered only while nothing is paid for. A hotel mid-period gets
             * one plan back from the server and never sees this, because
             * changing plan there is an upgrade with credit rather than a
             * free choice.
             */}
            {plans.length > 1 ? (
              <div className="col gap-6">
                <div className="b">Choose a plan</div>
                <div className="xs muted">
                  You have not paid for anything yet, so you can still change your mind.
                </div>
                <div className="plan-accordion" style={{ marginTop: 4 }}>
                  {plans.map((plan) => (
                    <button
                      type="button"
                      key={plan.id}
                      className={`plan-row-head${chosenPlan?.id === plan.id ? ' selected' : ''}`}
                      aria-pressed={chosenPlan?.id === plan.id}
                      onClick={() => {
                        setPlanId(plan.id);
                        // A period belongs to one plan; carrying the old
                        // choice across would price the wrong thing.
                        setSelected(null);
                        setAutoRenew(false);
                      }}
                    >
                      <span className="plan-radio" aria-hidden="true" />
                      <span className="grow">
                        <b>{plan.name}</b>
                        {plan.isCurrent ? (
                          <span className="chip ok" style={{ marginLeft: 6 }}>
                            Chosen at signup
                          </span>
                        ) : null}
                        <span className="xs muted plan-row-sub">
                          Up to {plan.maxProperties}{' '}
                          {plan.maxProperties === 1 ? 'property' : 'properties'} and {plan.maxUsers}{' '}
                          users
                        </span>
                      </span>
                      <span className="plan-row-price">
                        <b>{money(plan.priceMonthly)}</b>
                        <span className="plan-per">per month</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {available.length === 0 ? (
              <Empty message="No billing periods are offered for this plan yet." />
            ) : (
              <>
                <div className="col gap-6">
                  {plans.length > 1 ? (
                    <>
                      <div className="b" style={{ marginTop: 4 }}>
                        Then choose how long
                      </div>
                      <div className="xs muted" style={{ marginBottom: 2 }}>
                        Longer periods are billed once for the whole term.
                      </div>
                    </>
                  ) : null}
                  {available.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`period-row${selected === option.id ? ' selected' : ''}`}
                      aria-pressed={selected === option.id}
                      onClick={() => {
                        setSelected(option.id);
                        // A period that cannot recur must not carry the choice
                        // over from one that could.
                        if (!option.supportsRecurring) setAutoRenew(false);
                      }}
                    >
                      <span className="period-radio" aria-hidden="true" />
                      <span className="grow">
                        <b>{option.label}</b>
                        {option.supportsRecurring ? (
                          <span className="chip info" style={{ marginLeft: 6 }}>
                            Can auto-renew
                          </span>
                        ) : null}
                      </span>
                      <span className="b">{money(option.total)}</span>
                    </button>
                  ))}
                </div>

                {chosen ? (
                  <>
                    <label
                      className="row gap-6"
                      title={renewable ? undefined : 'This period is one-time only'}
                    >
                      <input
                        type="checkbox"
                        checked={wantsRenew}
                        disabled={!renewable}
                        onChange={(e) => setAutoRenew(e.target.checked)}
                      />
                      Automatically renew my subscription
                    </label>
                    <p className="small muted">
                      {wantsRenew
                        ? `By enabling automatic renewal you authorise recurring charges of ${money(chosen.total)} every ${chosen.label.toLowerCase().replace('ly', '')} period, according to your payment provider's requirements. You can turn this off at any time from this page.`
                        : 'Your subscription will not renew automatically — you will be asked before anything is charged again.'}
                    </p>

                    <div className="order-summary">
                      <div className="stat-line">
                        <span className="dim">Plan</span>
                        {/* The plan being bought, which is not the one they
                            are on when they have picked another. */}
                        <span>{chosenPlan?.name ?? data.planName}</span>
                      </div>
                      <div className="stat-line">
                        <span className="dim">Billing period</span>
                        <span>{chosen.label}</span>
                      </div>
                      <div className="stat-line">
                        <span className="dim">Payment</span>
                        <span>{wantsRenew ? 'Recurring' : 'One-time'}</span>
                      </div>
                      <div className="stat-line">
                        {/* "Plan price", not "Subtotal": there is one line
                            above the tax, so calling it a sub-total of
                            anything is accounting language for a number that
                            is simply what the plan costs. */}
                        <span className="dim">Plan price</span>
                        <span>{money(chosen.price)}</span>
                      </div>
                      {chosen.taxAmount > 0 ? (
                        <div className="stat-line">
                          <span className="dim">
                            {chosen.taxLabel} ({chosen.taxRate}%)
                          </span>
                          <span>{money(chosen.taxAmount)}</span>
                        </div>
                      ) : null}
                      <div className="stat-line total">
                        <span>Total today</span>
                        <b>{money(chosen.total)}</b>
                      </div>
                    </div>

                    {canPay ? (
                      <button
                        className="btn primary lg block"
                        disabled={checkout.isPending}
                        onClick={() =>
                          checkout.mutate({ billingOptionId: chosen.id, autoRenew: wantsRenew })
                        }
                      >
                        {checkout.isPending
                          ? 'Opening checkout…'
                          : wantsRenew
                            ? `Pay ${money(chosen.total)} and set up auto-renewal`
                            : `Pay ${money(chosen.total)} once`}
                      </button>
                    ) : (
                      <p className="small muted">
                        Ask an owner or administrator to complete the payment.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="small muted">Choose a billing period to see what it costs.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Invoices</h2>
        </div>
        {invoices.isLoading ? (
          <Loading />
        ) : (invoices.data?.length ?? 0) === 0 ? (
          <Empty message="No invoices yet." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Plan</th>
                  <th>Period</th>
                  <th>Payment</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                  <th>Issued</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(invoices.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="mono xs">{row.number}</td>
                    <td>{row.planName}</td>
                    <td className="small dim">
                      {date(row.periodStart)} — {date(row.periodEnd)}
                    </td>
                    <td className="small">
                      {row.paymentMode === 'RECURRING' ? 'Recurring' : 'One-time'}
                    </td>
                    <td className="num b">{money(row.total)}</td>
                    <td>
                      <span className={`chip ${row.status === 'PAID' ? 'ok' : 'danger'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="nowrap">{date(row.issuedAt)}</td>
                    <td className="num">
                      {/* The tax invoice itself, which until now existed only
                          as a row in this table. */}
                      <button className="btn ghost sm" onClick={() => setInvoice(row)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Payment history</h2>
        </div>
        {payments.isLoading ? (
          <Loading />
        ) : (payments.data?.length ?? 0) === 0 ? (
          <Empty message="Nothing has been charged yet." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                  <th>Reference</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {(payments.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="nowrap">{date(row.createdAt)}</td>
                    <td>
                      {row.type === 'RENEWAL' ? 'Renewal' : 'Subscription'}
                      <span className="xs muted">
                        {' '}
                        · {row.paymentMode === 'RECURRING' ? 'recurring' : 'one-time'}
                      </span>
                      {row.failureReason ? (
                        <div className="xs warn">{row.failureReason}</div>
                      ) : null}
                    </td>
                    <td className="num b">{money(row.amount)}</td>
                    <td>
                      <span
                        className={`chip ${row.status === 'SUCCESS' ? 'ok' : row.status === 'FAILED' ? 'danger' : ''}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="mono xs">{row.reference}</td>
                    <td className="mono xs">{row.invoiceNumber ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {invoice ? (
        <Modal
          title={`Invoice ${invoice.number}`}
          size="wide"
          onClose={() => setInvoice(null)}
          footer={
            <>
              <button className="btn" onClick={() => setInvoice(null)}>
                Close
              </button>
              {/* Print is also how somebody saves a PDF, which is why there
                  is no separate download: the browser's own dialog does it,
                  and does it better than a second rendering of the same
                  document would. */}
              <button className="btn primary" onClick={() => window.print()}>
                Print / Save as PDF
              </button>
            </>
          }
        >
          <div
            className="invoice-doc"
            dangerouslySetInnerHTML={{ __html: renderSubscriptionInvoice(invoice) }}
          />
        </Modal>
      ) : null}

      {stopping ? (
        <ConfirmDialog
          title="Turn off automatic renewal?"
          message="You keep every day you have already paid for. Nothing further will be charged, and you can buy another period whenever you want."
          confirmLabel="Turn it off"
          busy={cancelRenewal.isPending}
          onConfirm={() => cancelRenewal.mutate()}
          onCancel={() => setStopping(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * A promotional code, typed by a hotel that already exists.
 *
 * Codes used to work only at signup, so one sent afterwards - an apology, a
 * renewal incentive, something handed out at a trade show - had nowhere to go.
 *
 * Deliberately quiet until somebody opens it. A box asking for a code on a
 * page about paying invites everybody to go and look for one they do not
 * have, and a hotel that has been given a code knows it is looking for this.
 */
function RedeemCoupon({ onDone }: { onDone: () => void }) {
  const { fail, push } = useToast();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<{ benefitSummary: string; accessUntil: string } | null>(
    null,
  );

  const redeem = useMutation({
    mutationFn: () =>
      api.post<{ benefitSummary: string; accessUntil: string; daysAdded: number }>(
        '/api/billing/redeem-coupon',
        { code: code.trim().toUpperCase() },
      ),
    onSuccess: (result) => {
      setApplied(result);
      setCode('');
      push(`${result.benefitSummary} added`, 'ok');
      onDone();
    },
    // The server's message is written to be shown - expired, already used,
    // wrong plan - and is more use than anything this could guess at.
    onError: fail,
  });

  if (applied) {
    return (
      <p className="form-note ok">
        <b>{applied.benefitSummary} added to your subscription.</b> Your access now runs to{' '}
        {date(applied.accessUntil)}.
      </p>
    );
  }

  if (!open) {
    return (
      <button
        className="btn ghost sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => setOpen(true)}
      >
        Have a promotional code?
      </button>
    );
  }

  return (
    <div className="col gap-6">
      <div className="otp-row">
        <TextInput
          label="Promotional code"
          placeholder="DIWALI3M"
          value={code}
          autoFocus
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.trim()) redeem.mutate();
          }}
        />
        <button
          className="btn primary"
          disabled={!code.trim() || redeem.isPending}
          onClick={() => redeem.mutate()}
        >
          {redeem.isPending ? 'Checking…' : 'Redeem'}
        </button>
      </div>
      <span className="xs muted">
        The time is added to the end of what you already have — nothing you have paid for is lost.
      </span>
    </div>
  );
}
